import { useTheme } from "@mui/material";
import List from "@mui/material/List";
import { Box, Link, Text } from "designSystem/index";
import ListWithSubheader, { ListSection } from "designSystem/components/list/ListWithSubheader";
import { useLocale } from "designSystem/locale";
import { observer } from "mobx-react-lite";
import React, { FunctionComponent } from "react";
import { ChannelItem } from "src/Chat/api";
import NoChannelPlaceholder from "src/Chat/component/NoChannelPlaceholder";
import { useChannelListStyles } from "src/Chat/component/styles";
import { Channel, ChannelMembershipType } from "../api";
import ChannelInfo from "./ChannelInfo";
import LoadingMSTeams from "./LoadingMSTeams";
import { useMSTeamsStore } from "./MSTeamsStore";

const PrivateChannels = observer(() => {
	const {
		disconnectedPrivateChannels,
		fetchPrivateChannels,
		showFetchPrivateChannelButton,
		startLoadingPrivateChannel,
	} = useMSTeamsStore();

	return (
		<>
			{startLoadingPrivateChannel ?
				<LoadingMSTeams />
				:
				disconnectedPrivateChannels?.length == 0 ?
					<NoChannelPlaceholder />
					:
					disconnectedPrivateChannels?.map((channel) => {
						const channelItem = new ChannelItem<ChannelMembershipType>(
							channel.id,
							channel.displayName,
							channel.membershipType,
							channel.webUrl,
						);
						return (
							<ChannelInfo
								channel={channelItem}
								key={channel.id}
								showRadio
							/>
						);
					})
			}
			{showFetchPrivateChannelButton && (
				<Box p={3} pt={1}>
					<Link onClick={fetchPrivateChannels}>
						<Text res="ShowPrivateChannels" />
					</Link>
				</Box>
			)}
		</>
	);
});

interface ChannelsListProps {
	/** overall channels, only make senses in the case of connected channel pages */
	channels?: Channel[];

	/** public channels */
	publicChannels?: Channel[];

	/** flag to function like a radio button list */
	showRadio?: boolean;
}

const ChannelsList: FunctionComponent<ChannelsListProps> = observer(({
	channels,
	publicChannels,
	showRadio,
}) => {
	const { root, subtitleContainer, titleContainer } = useChannelListStyles(useTheme());
	const loc = useLocale();

	if (!showRadio && (null == channels || 0 === channels.length)) {
		return (
			<NoChannelPlaceholder />
		);
	}

	if (!showRadio) {
		// in the case of connect channels page
		return (
			<List component="nav" sx={root}>
				{
					channels.map((channel) => {
						const channelItem = new ChannelItem<ChannelMembershipType>(
							channel.id,
							channel.displayName,
							channel.membershipType,
							channel.webUrl,
						);
						return (
							<ChannelInfo
								channel={channelItem}
								key={channel.id}
								showRadio={showRadio}
							/>
						);
					})
				}
			</List>
		);
	}

	const publicSection: ListSection = {
		sectionTextStyles: titleContainer,
		sectionName: loc.resource("PublicChannels"),
		sectionBody: (
			<>
				{
					publicChannels?.map((channel) => {
						const channelItem = new ChannelItem<ChannelMembershipType>(
							channel.id,
							channel.displayName,
							channel.membershipType,
							channel.webUrl,
						);
						return (
							<ChannelInfo
								channel={channelItem}
								key={channel.id}
								showRadio={showRadio}
							/>
						);
					})
				}
			</>
		),
		subHeaderProps: { sx: subtitleContainer, disableGutters: true },
	};

	const privateSection: ListSection = {
		sectionTextStyles: titleContainer,
		sectionName: loc.resource("PrivateChannels"),
		sectionBody: <PrivateChannels />,
		subHeaderProps: { sx: subtitleContainer, disableGutters: true },
	};

	return (
		<ListWithSubheader sections={[publicSection, privateSection]} />
	);
});

export default ChannelsList;